export interface FieldError {
  code: string
  message: string
  params?: Record<string, unknown>
}

export type FieldErrors = Record<string, FieldError[]>

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fieldErrors: FieldErrors
  readonly requestId: string | undefined
  readonly retryAfter: number | undefined
  /** Machine-readable details of the error (e.g. `household_ids` of `owns_households`). */
  readonly params: Record<string, unknown>

  constructor(init: {
    status: number
    code: string
    message: string
    fieldErrors?: FieldErrors
    requestId?: string
    retryAfter?: number
    params?: Record<string, unknown>
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.fieldErrors = init.fieldErrors ?? {}
    this.requestId = init.requestId
    this.retryAfter = init.retryAfter
    this.params = init.params ?? {}
  }

  get isNetwork() {
    return this.code === 'network_error'
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError
}

function codeForStatus(status: number): string {
  switch (status) {
    case 401:
      return 'unauthenticated'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 419:
      return 'csrf_token_mismatch'
    case 422:
      return 'validation_failed'
    case 429:
      return 'too_many_requests'
    default:
      return status >= 500 ? 'server_error' : 'request_failed'
  }
}

export function networkError(cause?: unknown): ApiError {
  const error = new ApiError({ status: 0, code: 'network_error', message: 'Network error' })
  if (cause !== undefined) (error as { cause?: unknown }).cause = cause
  return error
}

/** Normalizes any failed HTTP response (JSON `Error` body or not) into ApiError. */
export function normalizeApiError(status: number, body: unknown, headers?: Headers): ApiError {
  const data = body && typeof body === 'object' ? (body as Record<string, unknown>) : {}
  const retryHeader = headers?.get('Retry-After')
  const retryAfter = retryHeader && /^\d+$/.test(retryHeader) ? Number(retryHeader) : undefined
  return new ApiError({
    status,
    code: typeof data.code === 'string' ? data.code : codeForStatus(status),
    message: typeof data.message === 'string' ? data.message : '',
    fieldErrors: isFieldErrors(data.field_errors) ? data.field_errors : {},
    requestId: typeof data.request_id === 'string' ? data.request_id : undefined,
    retryAfter,
    ...(data.params && typeof data.params === 'object' && !Array.isArray(data.params) ? { params: data.params as Record<string, unknown> } : {}),
  })
}

function isFieldErrors(value: unknown): value is FieldErrors {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}
